
import $ from "jquery"
import "plugins/jquery_plugins/bootstrap-carousel"
import LazyLoad from "vanilla-lazyload"

import AssetsInfoStore from "app/assets_info_store"

import DomIdGenerator from "app/utils/dom_id_generator"

import LeafletSettings from "app/map/leaflet/settings"

const INIT_COMPLETE_CLASS = "js-init-done"


interface Selector {
  carousel:             string
  slide_to_prev_button: string
  slide_to_next_button: string

  static_map_container: string
}
export interface InputSelector {
  carousel?:             string
  slide_to_prev_button?: string
  slide_to_next_button?: string

  static_map_container?: string
}
const default_selector = {
  carousel:             ".cell-listing-carousel--main",
  slide_to_prev_button: ".cell-listing-carousel__slide-to-prev-btn",
  slide_to_next_button: ".cell-listing-carousel__slide-to-next-btn",

  static_map_container: ".cell-listing-carousel__static-map-box",
}

export class ListingCarousel {
  private selector: Selector

  private container_element: Element
  private $container_el: JQuery
  private $carousel: JQuery
  private $slide_to_prev_button: JQuery
  private $slide_to_next_button: JQuery
  private $static_map_container: JQuery


  constructor({container_element, selector = {}}: {container_element: Element, selector: InputSelector}){
    if (container_element == null) {
      throw new Error("container_element is absent")
    }

    this.selector = Object.assign({}, default_selector, selector)
    this.container_element = container_element
    this.$container_el = $(container_element)

    this.$carousel = this.$container_el.find(this.selector.carousel)

    this.$slide_to_prev_button = this.$container_el.find(this.selector.slide_to_prev_button)
    this.$slide_to_next_button = this.$container_el.find(this.selector.slide_to_next_button)

    // Can be absent
    this.$static_map_container = this.$container_el.find(this.selector.static_map_container)

    this.created()
    this.bind_action()
  }

  public cycle(): void {
    this.$carousel.carousel("cycle")
  }

  protected created() {
    const lazy_loader = new LazyLoad({
      container:          this.container_element as HTMLElement,
      elements_selector:  "img[data-high-res-src]",
      data_src:           "high-res-src",
      use_native:         false,
    })
  }

  protected bind_action(){
    if (this.$carousel.hasClass(INIT_COMPLETE_CLASS)) { return this }
    if (this.$carousel.hasClass("carousel-paused")){
      this.$carousel.carousel("pause")
    }
    else {
      // carousel autoplay recommended to be disabled for web vitals
      // https://web.dev/carousel-best-practices/
      this.$carousel.carousel("pause")
    }

    this.$carousel.addClass(INIT_COMPLETE_CLASS)

    // region click behavior

    this.$slide_to_prev_button.on("click", (e) => {
      // Might be an anchor with hash in href
      e.preventDefault()

      this.$carousel.carousel("prev")
    })
    this.$slide_to_next_button.on("click", (e) => {
      // Might be an anchor with hash in href
      e.preventDefault()

      this.$carousel.carousel("next")
    })

    // endregion click behavior


    if (this.$static_map_container.length === 1) {
      const $map_container = this.$static_map_container
      const map_el = $map_container[0]

      const dom_id = DomIdGenerator.generateNewId()
      // Set new ID if applicable
      if (dom_id !== map_el.id) { map_el.setAttribute("id", dom_id) }

      const lat = parseFloat($map_container.data("lat"))
      const lng = parseFloat($map_container.data("lng"))

      // Generate ID for Leaflet map, if absent
      const map_canvas_dom_id = dom_id

      import(
        "plugins/leaflet"
      )
      .then(({L}) => {
        import(
          "app/map/leaflet/map_tile_layer"
        )
        .then(({LeafletMapTileLayer}) => {
          const center = L.latLng(lat, lng)

          // If we add map tile layer AFTER creating map object
          // The location of tile layer is drifted on load
          // To workaround this we create tile layer first and
          // pass it on map object creation
          LeafletMapTileLayer.getNewTileLayerPromise()
          .then((map_tile_layer) => {
            return AssetsInfoStore.fetch("map/markers/s-spacious.png")
            .then((icon_image_data) => {
              const leaflet_map =
                LeafletSettings.get_use_baidu_maps() ?
                  L.map(map_canvas_dom_id, {
                    center: center,

                    zoom:     17,
                    minZoom:  17,
                    maxZoom:  17,

                    // Interaction Options
                    dragging:         false,
                    touchZoom:        false,
                    scrollWheelZoom:  false,
                    doubleClickZoom:  false,
                    boxZoom:          false,
                    tap:              false,

                    // Control options
                    zoomControl:        false,
                    attributionControl: false,

                    layers: [
                      map_tile_layer,
                    ],
                  })
                  :
                  L.map(map_canvas_dom_id, {
                    center: center,

                    zoom:     17,
                    minZoom:  17,
                    maxZoom:  17,

                    // Interaction Options
                    dragging:         false,
                    touchZoom:        false,
                    scrollWheelZoom:  false,
                    doubleClickZoom:  false,
                    boxZoom:          false,
                    tap:              false,

                    // Control options
                    zoomControl:        false,
                    attributionControl: false,

                    layers: [
                      map_tile_layer,
                    ],
                  })

              const marker_icon = L.icon({
                iconUrl:  icon_image_data.url,
                iconSize: [25, 29],
              })
              L.marker(center, {
                icon: marker_icon,
              }).addTo(leaflet_map)
            })
          })
          .done()
        })
      })

    }

    return this
  }
}

const Factory = {
  create(container_element: Element, selector?: InputSelector){
    return new ListingCarousel({
      container_element:  container_element,
      selector:           (selector || {}),
    })
  },
}

export {
  Factory,
  Factory as default,
}
